import {
    Badge,
    BlockStack,
    Button,
    CalloutCard,
    Card,
    Icon,
    InlineGrid,
    InlineStack,
    Layout,
    Page,
    Text,
} from "@shopify/polaris";
import {
    ChartVerticalIcon,
    ImportIcon,
    XIcon,
} from "@shopify/polaris-icons";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import agent from "../../app/api/agent";
import { AtdDiagnostics } from "../../app/models/Atd";
import { useStore } from "../../app/stores/store";
import { getShopifyStoreHandle } from "../../app/utils/shopifyAdmin";
import DashboardOverview from "./DashboardOverview";
import DashboardSetupCard from "./DashboardSetupCard";
import { getLatestDashboardFeatureAnnouncement } from "./dashboardFeatureAnnouncements";
import vcdbReferenceCalloutImage from "../../assets/dashboard/polaris-vcdb-reference.png";

type DashboardMetricProps = {
    label: string;
    value: string | number;
    tone?: "subdued" | "critical";
    onClick?: () => void;
};

const formatDateTime = (value?: string) => {
    if (!value) return "Not synced yet";

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "Not synced yet";

    return parsed.toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
};

const formatCount = (value?: number) => (value ?? 0).toLocaleString();

const atdLoadingCardDelayMs = 500;
const showAtdDashboardCard = false;
const storeDisplayNameStorageKey = "partfits.dashboard.storeDisplayNames.v1";
const normalizeStoreDisplayName = (value?: string | null) => {
    const trimmed = value?.trim();
    if (!trimmed) return "";

    return trimmed.replace(/\.myshopify\.com$/i, "");
};

const getCachedStoreDisplayName = (shopHandle: string) => {
    if (!shopHandle) return "";

    try {
        const stored = window.sessionStorage.getItem(storeDisplayNameStorageKey);
        if (!stored) return "";

        const displayNames = JSON.parse(stored) as Record<string, string>;
        return normalizeStoreDisplayName(displayNames[shopHandle]);
    } catch {
        return "";
    }
};

const cacheStoreDisplayName = (shopHandle: string, displayName: string) => {
    if (!shopHandle || !displayName) return;

    try {
        const stored = window.sessionStorage.getItem(storeDisplayNameStorageKey);
        const displayNames = stored ? JSON.parse(stored) as Record<string, string> : {};
        displayNames[shopHandle] = displayName;
        window.sessionStorage.setItem(storeDisplayNameStorageKey, JSON.stringify(displayNames));
    } catch {
        // A missing cache should never block rendering the dashboard.
    }
};

const DashboardMetric = ({ label, value, tone, onClick }: DashboardMetricProps) => {
    const content = (
        <>
            <Text as="p" variant="bodySm" tone="subdued">
                {label}
            </Text>
            <Text as="p" variant="headingLg" tone={tone}>
                {value}
            </Text>
        </>
    );

    if (onClick) {
        return (
            <button type="button" className="pf-dashboard-metric pf-dashboard-metric--button" onClick={onClick}>
                {content}
            </button>
        );
    }

    return (
        <div className="pf-dashboard-metric">
            {content}
        </div>
    );
};

const Dashboard = () => {
    const navigate = useNavigate();
    const { userStore } = useStore();
    const [atdDiagnostics, setAtdDiagnostics] = useState<AtdDiagnostics | undefined>();
    const [atdLoading, setAtdLoading] = useState(true);
    const [showAtdLoadingCard, setShowAtdLoadingCard] = useState(false);
    const [atdError, setAtdError] = useState<string | undefined>();
    const [vcdbReferenceCalloutLoaded, setVcdbReferenceCalloutLoaded] = useState(false);
    const [showVcdbReferenceCallout, setShowVcdbReferenceCallout] = useState(false);
    const [dismissedFeatureAnnouncementId, setDismissedFeatureAnnouncementId] = useState("");

    useEffect(() => {
        if (!userStore.user) {
            userStore.getUser();
        }
    }, [userStore, userStore.user]);

    useEffect(() => {
        let cancelled = false;

        const loadDashboardSettings = async () => {
            try {
                const settings = await agent.Settings.dashboard();
                if (!cancelled) {
                    setShowVcdbReferenceCallout(!settings.vcdbReferenceCalloutDismissed);
                    setDismissedFeatureAnnouncementId(settings.dismissedFeatureAnnouncementId || "");
                }
            } catch {
                // Keep the reference-data prompt visible if settings cannot load,
                // but only for the current render; persistence remains DB-backed.
                if (!cancelled) {
                    setShowVcdbReferenceCallout(true);
                }
            } finally {
                if (!cancelled) {
                    setVcdbReferenceCalloutLoaded(true);
                }
            }
        };

        loadDashboardSettings();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadAtdDiagnostics = async () => {
            if (!showAtdDashboardCard) {
                setAtdLoading(false);
                return;
            }

            try {
                setAtdLoading(true);
                setAtdError(undefined);
                const diagnostics = await agent.Atd.diagnostics();
                if (!cancelled) setAtdDiagnostics(diagnostics);
            } catch (error: any) {
                if (!cancelled) {
                    setAtdDiagnostics(undefined);
                    setAtdError(error?.message || "Unable to load ATD status.");
                }
            } finally {
                if (!cancelled) setAtdLoading(false);
            }
        };

        loadAtdDiagnostics();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!atdLoading) {
            setShowAtdLoadingCard(false);
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setShowAtdLoadingCard(true);
        }, atdLoadingCardDelayMs);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [atdLoading]);

    const currentYear = new Date().getFullYear();
    const storeHandle = getShopifyStoreHandle(userStore.user?.shopDomain || atdDiagnostics?.shopDomain);
    const explicitStoreDisplayName = normalizeStoreDisplayName(userStore.user?.shopName)
        || normalizeStoreDisplayName(atdDiagnostics?.shopName);
    const domainStoreDisplayName = normalizeStoreDisplayName(userStore.user?.shopDomain)
        || normalizeStoreDisplayName(atdDiagnostics?.shopDomain);
    const storeDisplayName = explicitStoreDisplayName
        || getCachedStoreDisplayName(storeHandle)
        || domainStoreDisplayName
        || storeHandle;

    useEffect(() => {
        cacheStoreDisplayName(storeHandle, explicitStoreDisplayName);
    }, [explicitStoreDisplayName, storeHandle]);

    const atdStatusTone = atdLoading ? "info" : atdDiagnostics?.profileActive ? "success" : atdDiagnostics?.profileConfigured ? "attention" : "critical";
    const atdStatusLabel = atdLoading
        ? "Loading"
        : atdDiagnostics?.profileActive
            ? "Active"
            : atdDiagnostics?.profileConfigured
                ? "Configured, inactive"
                : "Not configured";
    const failedOrders = atdDiagnostics?.failedOrderCount ?? 0;
    const atdLinkedProductsPath = "/products?tab=atd&linkStatus=linked";
    const atdUnlinkedProductsPath = "/products?tab=atd&linkStatus=unlinked";
    const showAtdCard = showAtdDashboardCard && (Boolean(atdDiagnostics?.profileConfigured) || Boolean(atdError) || (atdLoading && showAtdLoadingCard));
    const activeFeatureAnnouncement = vcdbReferenceCalloutLoaded
        ? getLatestDashboardFeatureAnnouncement(dismissedFeatureAnnouncementId)
        : undefined;
    // This replaces the old stale-inventory count with an action-oriented
    // catalog health signal: products linked in Shopify vs products currently
    // available from ATD's latest catalog sync.
    const linkedAvailableProducts = atdLoading
        ? "-"
        : `${formatCount(atdDiagnostics?.linkedProductCount)} / ${formatCount(atdDiagnostics?.availableProductCount ?? atdDiagnostics?.productCount)}`;
    const dismissVcdbReferenceCallout = async () => {
        setShowVcdbReferenceCallout(false);

        try {
            await agent.Settings.dismissVcdbReferenceCallout();
        } catch {
            // Leave it hidden for the active session; a later page load will
            // show it again if the DB save did not complete.
        }
    };
    const dismissFeatureAnnouncement = async () => {
        if (!activeFeatureAnnouncement) return;

        const announcementId = activeFeatureAnnouncement.id;
        setDismissedFeatureAnnouncementId(announcementId);

        try {
            await agent.Settings.dismissFeatureAnnouncement(announcementId);
        } catch {
            // Keep the announcement hidden for the active session. If the
            // setting did not persist, it can be shown again on a later load.
        }
    };
    return (
        <Page>
            <div className="pf-dashboard">
                <Layout>
                    <Layout.Section>
                        <BlockStack gap="500">
                            <header className="pf-dashboard__heading">
                                <BlockStack gap="100">
                                    <Text as="h1" variant="headingXl">{storeDisplayName || 'PartFits'}</Text>
                                    <Text as="p" tone="subdued">Your fitment catalog and storefront activity at a glance.</Text>
                                </BlockStack>
                                <Button variant="primary" icon={ImportIcon} onClick={() => navigate("/imports")}>
                                    Import fitment
                                </Button>
                            </header>

                            <DashboardSetupCard />
                            <DashboardOverview />

                            {vcdbReferenceCalloutLoaded && showVcdbReferenceCallout ? (
                                <div className="pf-dashboard__vcdb-callout">
                                    <CalloutCard
                                        title="Optional vehicle reference data is available"
                                        illustration={vcdbReferenceCalloutImage}
                                        primaryAction={{
                                            content: "Review reference data",
                                            onAction: () => navigate("/storefront?section=reference-data"),
                                        }}
                                        onDismiss={dismissVcdbReferenceCallout}
                                    >
                                        <p>
                                            PartFits can add standard vehicle values to your admin selectors, giving you a broader starting point when creating fitments. This is turned off by default, so your dropdowns only show vehicles already saved in your fitment data.
                                        </p>
                                    </CalloutCard>
                                </div>
                            ) : null}

                            {showAtdCard ? (
                                <Card>
                                    <BlockStack gap="400">
                                        <InlineStack align="space-between" blockAlign="center" gap="300">
                                            <InlineStack gap="200" blockAlign="center">
                                                <Text as="h2" variant="headingMd">
                                                    ATD integration
                                                </Text>
                                                <Badge tone={atdStatusTone}>{atdStatusLabel}</Badge>
                                            </InlineStack>
                                            <InlineStack gap="200">
                                                <Button onClick={() => navigate("/integrations/atd/orders")}>View orders</Button>
                                                <Button onClick={() => navigate("/integrations/atd/profile")}>Settings</Button>
                                            </InlineStack>
                                        </InlineStack>

                                        <Text as="p" tone="subdued">
                                            ATD product sync, fitment coverage, and Ship-To-Home fulfillment status.
                                        </Text>

                                        {atdError ? (
                                            <Text as="p" tone="critical">
                                                {atdError}
                                            </Text>
                                        ) : atdLoading ? (
                                            <Text as="p" tone="subdued">
                                                Loading ATD integration status...
                                            </Text>
                                        ) : null}

                                        <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="300">
                                            <DashboardMetric
                                                label="Products linked to ATD"
                                                value={linkedAvailableProducts}
                                                onClick={() => navigate(atdLinkedProductsPath)}
                                            />
                                            <DashboardMetric
                                                label="ATD orders in progress"
                                                value={atdLoading ? "-" : atdDiagnostics?.inProgressOrderCount ?? 0}
                                            />
                                            <DashboardMetric
                                                label="ATD orders, last 30 days"
                                                value={atdLoading ? "-" : atdDiagnostics?.ordersLast30Days ?? 0}
                                            />
                                            <DashboardMetric
                                                label="Orders needing attention"
                                                value={atdLoading ? "-" : failedOrders}
                                                tone={failedOrders > 0 ? "critical" : undefined}
                                                onClick={() => navigate("/integrations/atd/orders?status=NeedsAttention")}
                                            />
                                        </InlineGrid>

                                        <div className="pf-atd-card__footer">
                                            <Text as="span" tone="subdued">
                                                Last ATD catalog sync: {formatDateTime(atdDiagnostics?.lastCatalogSyncAt)}
                                            </Text>
                                            {atdDiagnostics?.stragglerProductCount ? (
                                                <Button onClick={() => navigate(atdUnlinkedProductsPath)}>
                                                    {`Review ${formatCount(atdDiagnostics.stragglerProductCount)} unlinked product${atdDiagnostics.stragglerProductCount === 1 ? "" : "s"}`}
                                                </Button>
                                            ) : null}
                                        </div>
                                    </BlockStack>
                                </Card>
                            ) : null}

                            {activeFeatureAnnouncement ? (
                                <Card>
                                    <div className="pf-dashboard-feature-announcement">
                                        <span className="pf-dashboard-feature-announcement__icon" aria-hidden="true">
                                            <Icon source={ChartVerticalIcon} />
                                        </span>

                                        <BlockStack gap="150">
                                            <InlineStack gap="200" blockAlign="center" wrap>
                                                <Badge tone="info">New</Badge>
                                                <Text as="h2" variant="headingMd">
                                                    {activeFeatureAnnouncement.title}
                                                </Text>
                                            </InlineStack>
                                            <Text as="p" tone="subdued">
                                                {activeFeatureAnnouncement.description}
                                            </Text>
                                        </BlockStack>

                                        <div className="pf-dashboard-feature-announcement__actions">
                                            <Button
                                                variant="primary"
                                                onClick={() => navigate(activeFeatureAnnouncement.actionPath)}
                                            >
                                                {activeFeatureAnnouncement.actionLabel}
                                            </Button>
                                            <Button
                                                accessibilityLabel="Dismiss feature announcement"
                                                icon={XIcon}
                                                variant="tertiary"
                                                onClick={dismissFeatureAnnouncement}
                                            />
                                        </div>
                                    </div>
                                </Card>
                            ) : null}


                        </BlockStack>
                    </Layout.Section>
                </Layout>

                <div className="pf-dashboard__footer">
                    &copy; PartFits {currentYear}
                </div>

            </div>
        </Page>
    );
};

export default observer(Dashboard);
